import { useCallback } from 'react';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useFieldBindingState } from '@dealstack/frontend/shared/components/FieldBinding/useFieldBindingState.ts';
import type {
	FieldBindingDto,
	FieldBindingResult,
} from '@dealstack/frontend/shared/components/FieldBinding/useFieldBindingState.types.ts';
import { useGlobalFieldBindingsQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalFieldBindingsQuery';
import { useGlobalDataPointCatalogueQuery } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useGlobalDataPointCatalogueQuery';
import { useUpdateGlobalFieldBindings } from '@dealstack/frontend/app/pages/DocumentTemplates/api/useUpdateGlobalFieldBindings';

export const useGlobalFieldBinding = (templateId: string | null): FieldBindingResult => {
	const tDefault = useTranslationDefault();
	const { data: bindingsData, isLoading: isLoadingBindings } =
		useGlobalFieldBindingsQuery(templateId);
	const { data: catalogueData, isLoading: isLoadingCatalogue } =
		useGlobalDataPointCatalogueQuery(templateId);
	const updateBindings = useUpdateGlobalFieldBindings();

	return useFieldBindingState({
		serverBindings: bindingsData,
		// The endpoint now answers with the same wrapper the deal one does; `securities` and
		// `scopedSamples` are always empty here, which is what suppresses the security picker.
		catalogue: catalogueData?.dataPoints,
		isLoading: isLoadingBindings || isLoadingCatalogue,
		isSaving: updateBindings.isPending,
		newFieldLabel: tDefault('document_templates.field_binding.new_field_default'),
		onSave: useCallback(
			(bindings: FieldBindingDto[], onSaved: () => void) => {
				if (!templateId) {
					return;
				}
				updateBindings.mutate({ templateId, bindings }, { onSuccess: onSaved });
			},
			[templateId, updateBindings]
		),
	});
};
